import { JsonLd } from "@/components/JsonLd";
import type { Location } from "@/content/locations";
import { SITE } from "@/content/site";
import { breadcrumbSchema } from "@/lib/seo";
import { faqSchema, cityLocalBusinessSchema } from "@/lib/schema";

/** Services listed in the city hub's Service schema offer catalog. */
const CITY_OFFER_CATALOG = [
  "Local SEO & map-pack optimization",
  "Google Business Profile management",
  "Technical SEO audits",
  "On-page & content SEO",
  "Bangla + English keyword strategy",
  "AEO & GEO for AI answer engines",
];

/**
 * LocalBusiness for the city. Only Rangpur — where the business is actually
 * based — carries the street address and postal code.
 */
function cityHubLocalBusiness(loc: Location) {
  const base = cityLocalBusinessSchema({
    city: loc.city,
    region: loc.region,
    country: loc.country,
    countryCode: loc.countryCode,
    geo: loc.geo,
    path: `/seo-services/${loc.slug}`,
  });
  if (loc.slug !== "rangpur") return base;
  return {
    ...base,
    address: {
      ...base.address,
      streetAddress: SITE.streetAddress,
      postalCode: SITE.postalCode,
    },
  };
}

/** All JSON-LD for a city hub: LocalBusiness, FAQ, Place, Service and breadcrumb. */
export function CityHubStructuredData({ loc }: { loc: Location }) {
  const url = `${SITE.url}/seo-services/${loc.slug}`;
  return (
    <>
      <JsonLd data={cityHubLocalBusiness(loc)} />
      <JsonLd data={faqSchema(loc.faq)} />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Place",
          "@id": `${url}#place`,
          name: `${loc.city}, ${loc.region}`,
          address: {
            "@type": "PostalAddress",
            addressLocality: loc.city,
            addressRegion: loc.region,
            addressCountry: loc.countryCode,
          },
          geo: {
            "@type": "GeoCoordinates",
            latitude: loc.geo.lat,
            longitude: loc.geo.lng,
          },
          containsPlace: loc.neighborhoods.map((n) => ({
            "@type": "Place",
            name: n,
          })),
        }}
      />
      <JsonLd
        data={{
          "@context": "https://schema.org",
          "@type": "Service",
          "@id": `${url}#service`,
          serviceType: "Search Engine Optimization",
          name: `SEO Services in ${loc.city}`,
          description: `Local SEO, Google Business Profile optimization, technical SEO, and content strategy for ${loc.city} businesses.`,
          provider: { "@id": `${SITE.url}/#person` },
          areaServed: {
            "@type": "City",
            name: loc.city,
            address: {
              "@type": "PostalAddress",
              addressRegion: loc.region,
              addressCountry: loc.countryCode,
            },
          },
          hasOfferCatalog: {
            "@type": "OfferCatalog",
            name: `${loc.city} SEO service catalog`,
            itemListElement: CITY_OFFER_CATALOG.map((n) => ({
              "@type": "Offer",
              itemOffered: { "@type": "Service", name: n },
            })),
          },
        }}
      />
      <JsonLd
        data={breadcrumbSchema([
          { name: "Home", path: "/" },
          { name: "SEO Locations", path: "/seo-locations" },
          { name: loc.city, path: `/seo-services/${loc.slug}` },
        ])}
      />
    </>
  );
}
